import React from 'react';
import './PageTitleBar.css';

/**
 * PageTitleBar
 *
 * Props:
 *  - title       {string}   Page heading text (e.g. "Dashboard", "Sites")
 *  - setView     {func}     Navigation callback received from the wrapper in App.js
 *  - activeBtn   {string}   Which button should appear "active" (matches a button's `view` value)
 *
 * Buttons config:
 *  Each entry: { label, view }
 *    label  – text shown on the button
 *    view   – value passed to setView() when clicked
 *             If view === null the button has no click action (e.g. "Export Reports")
 *             If view === activeBtn the button renders with the active style
 */
const NAV_BUTTONS = [
  { label: 'Home', view: 'home' },
  { label: 'Dashboard', view: 'dashboard' },
  { label: 'Analytics', view: 'analytics' },
  { label: 'Export Reports', view: null },
  { label: 'Asset', view: 'assets-list' },
  { label: 'Supplier', view: 'supplier-list' },
  { label: 'Pole Design', view: 'pole-designs' },
  { label: 'Maintenance', view: 'maintenance' },
];

const PageTitleBar = ({ title, setView, activeBtn }) => {
  const userStr = localStorage.getItem('user');
  let userRole = null;
  try {
    const user = JSON.parse(userStr);
    userRole = user?.role;
  } catch (e) {
    userRole = null;
  }

  const filteredButtons = NAV_BUTTONS.filter(({ view, label }) => {
    if (view === 'home') return true;

    const normRole = userRole ? userRole.replace(/[-_ ]/g, '').toLowerCase() : '';

    // Analytics is only visible to Super Admin role
    if (view === 'analytics' && normRole !== 'superadmin') {
      return false;
    }

    if (normRole === 'technicalsupport') return true;
    if (normRole === 'superadmin') return true;
    if (normRole === 'government') return view === 'dashboard';
    if (normRole === 'maintenance' || normRole === 'research') {
      return view === 'dashboard' || view === 'maintenance';
    }
    if (normRole === 'assetmanagement') {
      return view !== 'maintenance' && label !== 'Export Reports';
    }
    return true; // fallback
  });

  const isButtonActive = (btnView, btnLabel) => {
    if (!activeBtn) return false;
    const a = activeBtn.toLowerCase();
    const v = (btnView || '').toLowerCase();
    const l = (btnLabel || '').toLowerCase();

    if (v === a || l === a) return true;
    if ((v === 'home' || l === 'home') && a === 'home') return true;
    if ((v === 'assets-list' || l === 'asset' || l === 'assets') &&
      ['asset', 'assets', 'assets-list', 'sites', 'locations', 'categories', 'assets-dashboard'].includes(a)) {
      return true;
    }
    if ((v === 'supplier-list' || l === 'supplier' || l === 'suppliers') &&
      ['supplier', 'suppliers', 'supplier-list'].includes(a)) {
      return true;
    }
    if ((v === 'pole-designs' || l === 'pole design' || l === 'pole designs') &&
      ['pole design', 'pole designs', 'pole-design', 'pole-designs', 'design-components', 'components'].includes(a)) {
      return true;
    }
    return false;
  };

  return (
    <div className="ptb-root">
      <h1 className="ptb-h1">{title}</h1>

      <div className="ptb-actions">
        {/* Navigation buttons commented out as requested - preserved for future use
        {filteredButtons.map(({ label, view }) => {
          const isActive = isButtonActive(view, label);
          return (
            <button
              key={label}
              className={isActive ? 'ptb-btn-active' : 'ptb-btn-outline'}
              onClick={() => view && setView && setView(view)}
              disabled={!view}
            >
              {label}
            </button>
          );
        })}
        */}
        {activeBtn !== 'home' && title?.toLowerCase() !== 'home' && (
          <button
            className="ptb-btn-outline"
            onClick={() => setView && setView('home')}
            title="Back to Home"
          >
            ← Home
          </button>
        )}
      </div>
    </div>
  );
};

export default PageTitleBar;
